import { sortItemsByPredefinedOrder } from "@components/inputs/select/SelectAPI";
import { enhanceFieldVisibility, TGetValueFn, } from "@components/smart/FieldInfo";
import {
    EntitySetName,
    EntityTypeName,
    PrMaternityLeaveEntitlementEntity,
    PrMaternityLeaveEventEntity,
    PrSocialEventEntity
} from "@odata/GeneratedEntityTypes";
import { PrMaternityLeaveEntitlementCode, PrSocialEventTypeCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import MaternityLeaveFormView from "@pages/payroll/socialEvents/eventTypes/MaternityLeaveFormView";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import { getOtherPropertyGetter } from "@pages/payroll/socialEvents/SocialEvents.utils";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, GroupedField } from "../../../../enums";
import { addFormDef, getFormGroup } from "../../../../views/formView/Form.utils";
import {
    addChildDefinitions,
    addRequiredActionSummaryDef,
    addSubmissionTableDefinitions,
    getDefinitions as commonDef,
    getSocialEventsFormDef
} from "../SocialEventsDef";

export function isOfMaternityType(type: PrMaternityLeaveEntitlementCode): TGetValueFn<boolean> {
    return ({ storage }) => {
        const current = storage.getValueByPath(PrMaternityLeaveEventEntity.MaternityLeaveEntitlement, { useDirectValue: false });
        return current === type;
    };
}

const isOtherReasonSelectedThanGivingBirth: TGetValueFn<boolean> = ({ storage }) => {
    const reason = storage.getValueByPath(PrMaternityLeaveEventEntity.MaternityLeaveEntitlement, { useDirectValue: false });
    return !!reason && reason !== PrMaternityLeaveEntitlementCode.GivingBirth;
};

export const maternityLeaveEntitlementTransformation = sortItemsByPredefinedOrder([
    PrMaternityLeaveEntitlementCode.GivingBirth,
    PrMaternityLeaveEntitlementCode.AuthorityDecision,
    PrMaternityLeaveEntitlementCode.Agreement,
    PrMaternityLeaveEntitlementCode.Illness,
    PrMaternityLeaveEntitlementCode.DeceasedMother
]);

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const d = cloneDeep(getSocialEventsFormDef(MaternityLeaveFormView, PrSocialEventTypeCode.MaternityLeave, context));
    const generalGroup = getFormGroup(d, SocialEventGroup.General);

    addRequiredActionSummaryDef(d);

    // add Reason and DateEventStart to general group
    addFormDef(d, PrMaternityLeaveEventEntity.MaternityLeaveEntitlement, {
        type: FieldType.ComboBox,
        width: BasicInputSizes.L,
        label: i18next.t("SocialEvents:Form.MaternityLeaveEntitlement"),
        fieldSettings: {
            displayName: PrMaternityLeaveEntitlementEntity.Name,
            transformFetchedItems: maternityLeaveEntitlementTransformation
        },
        affectedFields: [
            { id: PrMaternityLeaveEventEntity.NumberTheirs },
        ]
    });
    addFormDef(d, PrMaternityLeaveEventEntity.DateEventStart, {
        type: FieldType.Date,
        groupedField: GroupedField.MultiStart,
        label: i18next.t("SocialEvents:Form.DateEventStart"),
        affectedFields: [
            { id: PrMaternityLeaveEventEntity.DateEventEnd, revalidate: true },
            { id: PrMaternityLeaveEventEntity.DateOfChildbirth }
        ]
    });
    addFormDef(d, PrMaternityLeaveEventEntity.DateEventEnd, {
        type: FieldType.Date,
        isRequired: true,
        groupedField: GroupedField.MultiEnd,
        label: i18next.t("SocialEvents:Form.DateEventEnd"),
        fieldSettings: {
            minDate: getOtherPropertyGetter(PrSocialEventEntity.DateEventStart)
        },
    });
    addFormDef(d, PrMaternityLeaveEventEntity.DateOfChildbirth, {
        type: FieldType.Date,
        fieldSettings: {
            previewValue: getOtherPropertyGetter(PrSocialEventEntity.DateEventStart)
        }
    });

    generalGroup.rows.splice(1, 0, [
        { id: PrMaternityLeaveEventEntity.MaternityLeaveEntitlement },
        { id: PrMaternityLeaveEventEntity.DateEventStart },
        { id: PrMaternityLeaveEventEntity.DateEventEnd },
        { id: PrMaternityLeaveEventEntity.DateOfChildbirth },
    ]);

    // enhance NumberTheirs visibility: only for GivingBirth entitlement
    enhanceFieldVisibility(d, PrMaternityLeaveEventEntity.NumberTheirs, isOfMaternityType(PrMaternityLeaveEntitlementCode.GivingBirth));

    // add Child group to definition, including fields
    addChildDefinitions(d, PrMaternityLeaveEventEntity, true, isOtherReasonSelectedThanGivingBirth);

    // Table tabs on the end of the form
    addSubmissionTableDefinitions(d);

    return {
        ...d,
        entitySet: EntitySetName.PrMaternityLeaveEvents
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    getEnumNameSpaceName(EntityTypeName.PrMaternityLeaveEntitlement)
];
setDefByEntityType(EntityTypeName.PrMaternityLeaveEvent, getDefinitions);